import { useState } from "react";
import {
  Box,
  Container,
  IconButton,
  Paper,
  Stack,
  SxProps,
  Tab,
  Tabs,
  Theme,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Pencil } from "lucide-react";
import type { DeceasedPerson } from "../../../../../../../bookings/types/edit-booking";
import StreamViewer from "../../../shared-ui/StreamViewer";
import MemoriesTab from "../../../tabs/MemoriesTab";
import type { ICommentType } from "../../../tabs/MemoriesTab";
import ObituaryTab from "../../../tabs/OrbituaryTab";
import DonationsTab from "../../../tabs/DonationTab";
import FavouritesTab from "../../../tabs/FavouritesTab";
import TimelineTab from "../../../tabs/TimelineTab";
import GalleryTab from "../../../tabs/GalleryTab";
import { DownloadsTab } from "../../../tabs/DownloadsTab";
import CommentCard from "../../../shared-ui/CommentCard";
import type {
  IStreamDetails,
  IThemeConfig,
} from "../../../types/memorial-types";
import { tokens } from "../../../../../../../../theme";
import BrandedHeader from "./components/BrandedHeader";
import ShareRail from "./components/ShareRail";
import RegisterBanner from "./components/RegisterBanner";
import type { CalendarEvent } from "../../../types/AddtoCalendarutils";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  sx?: SxProps<Theme>;
}

function TabPanel({ children, value, index, sx, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`funeral-home-tabpanel-${index}`}
      aria-labelledby={`funeral-home-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ width: "100%", p: 0, m: 0, ...(sx || {}) }}>{children}</Box>}
    </div>
  );
}

export interface FuneralHomeTemplateProps {
  streams: IStreamDetails[];
  activeStreamIndex: number;
  currentStream: IStreamDetails;
  onStreamChange: (index: number) => void;
  memorialTheme: IThemeConfig;
  deceasedData: DeceasedPerson | null;
  isLoadingDeceased: boolean;
  messages: ICommentType[];
  isAdmin: boolean;
  socketRef: React.RefObject<WebSocket | null>;
  handleApprove: (id: string, approve: boolean) => void;
  handleDelete: (id: string) => void;
  onMessageSent: () => void;

  currentPage: number;
  totalCount: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;

  funeralHomeName: string;
  funeralHomeLogo?: string | null;
  funeralHomeColor?: string;
  funeralHomeWebsite?: string | null;
  onBannerToggled?: () => void;
  calendarEvent?: CalendarEvent | null;
  fetchStreamDetails: () => Promise<void>;
  donationRefreshKey?: number;
}

const DEFAULT_FUNERAL_HOME_COLOR = "#614338";

export default function FuneralHomeTemplate({
  streams,
  activeStreamIndex,
  currentStream,
  onStreamChange,
  memorialTheme,
  deceasedData,
  isLoadingDeceased,
  messages,
  isAdmin,
  socketRef,
  handleApprove,
  handleDelete,
  onMessageSent,
  currentPage,
  totalCount,
  pageSize,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  funeralHomeName,
  funeralHomeLogo,
  funeralHomeColor,
  funeralHomeWebsite,
  onBannerToggled,
  calendarEvent,
  fetchStreamDetails,
  donationRefreshKey,
}: FuneralHomeTemplateProps) {
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const brandColor = funeralHomeColor || DEFAULT_FUNERAL_HOME_COLOR;
  const bannerEnabled = currentStream.funeral_home_banner_enabled !== false;
  const [isEditingObituary, setIsEditingObituary] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const hasObituaryText = Boolean(
    deceasedData?.orbituary &&
      deceasedData.orbituary.replace(/<[^>]*>/g, "").trim(),
  );
  const showEditPencil = isAdmin && hasObituaryText && !isEditingObituary;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fff" }}>
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "1200px",
          width: "100%",
          mx: "auto",
          pb: 4,
        }}
      >
        <BrandedHeader
          stream={currentStream}
          deceasedData={deceasedData}
          funeralHomeName={funeralHomeName}
          funeralHomeLogo={funeralHomeLogo}
          funeralHomeColor={brandColor}
        />
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          sx={{ mt: 2 }}
        >
          {!isMobile && (
            <ShareRail
              websiteStreamLink={currentStream.website_stream_link}
              title={currentStream.title}
              accentColor={brandColor}
              calendarEvent={calendarEvent}
            />
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StreamViewer
              streams={streams}
              activeStream={activeStreamIndex}
              onStreamChange={onStreamChange}
              memorialTheme={memorialTheme}
              currentStream={currentStream}
              deceasedName={deceasedData?.first_name}
              hideTabs={streams.length <= 1}
            />
          </Box>
        </Stack>

        <RegisterBanner
          funeralHomeName={funeralHomeName}
          funeralHomeColor={brandColor}
          websiteUrl={funeralHomeWebsite}
          isAdmin={isAdmin}
          streamId={currentStream.id}
          enabled={bannerEnabled}
          onToggled={onBannerToggled}
        />

        <Box
          sx={{
            mt: 3,
            borderRadius: "20px",
            border: "1px solid",
            borderColor: isMobile ? alpha(brandColor, 0.45) : "divider",
            ...(isMobile && { borderWidth: 2 }),
            p: { xs: 2, md: 3 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: "50px",
              bgcolor: alpha(brandColor, 0.06),
            }}
          >
            <Tabs
              value={tabValue}
              onChange={(_, value) => setTabValue(value)}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-flexContainer": {
                  width: "100%",
                  justifyContent: "space-between",
                },
                "& .MuiTab-root": {
                  flex: 1,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 400,
                  py: 1.5,
                  px: 2,
                  minHeight: "auto",
                  "&:hover": { color: brandColor },
                },
                "& .Mui-selected": {
                  color: "#fff !important",
                  backgroundColor: brandColor,
                  borderRadius: "50px",
                },
                "& .MuiTabs-indicator": { display: "none" },
              }}
            >
              <Tab label="Obituary" />
              <Tab label="Memories" />
              <Tab label="Donations" />
              <Tab label="Favourites" />
              <Tab label="Timeline" />
              <Tab label="Gallery" />
              <Tab label="Downloads" />
            </Tabs>
          </Paper>

          <TabPanel value={tabValue} index={0}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <Typography variant="h6" fontWeight={700}>
                Obituary
              </Typography>
              {showEditPencil && (
                <IconButton
                  size="small"
                  onClick={() => setIsEditingObituary(true)}
                  sx={{
                    bgcolor: alpha(brandColor, 0.12),
                    color: brandColor,
                    "&:hover": { bgcolor: alpha(brandColor, 0.22) },
                  }}
                >
                  <Pencil size={14} />
                </IconButton>
              )}
            </Stack>
            <ObituaryTab
              deceasedData={deceasedData}
              streamData={currentStream}
              isLoadingDeceased={isLoadingDeceased}
              isAdmin={isAdmin}
              accentColor={brandColor}
              variant="embedded"
              isEditing={isEditingObituary}
              onEditingChange={setIsEditingObituary}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Memories
            </Typography>
            <MemoriesTab
              messages={messages}
              isAdmin={isAdmin}
              socketRef={socketRef}
              isMobile={isMobile}
              handleApprove={handleApprove}
              handleDelete={handleDelete}
              CommentCard={CommentCard}
              colors={colors}
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={pageSize}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              onNextPage={onNextPage}
              onPreviousPage={onPreviousPage}
              onMessageSent={onMessageSent}
              variant="embedded"
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <DonationsTab
              streamData={currentStream}
              streamId={currentStream?.id?.toString() || ""}
              isAdmin={isAdmin}
              isMobile={isMobile}
              fetchStreamDetails={fetchStreamDetails}
              donationRefreshKey={donationRefreshKey}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <FavouritesTab
              deceasedId={deceasedData?.id}
              deceasedFirstName={deceasedData?.first_name}
              accentColor={brandColor}
              isAdmin={isAdmin}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <TimelineTab
              deceasedId={deceasedData?.id}
              deceasedFirstName={deceasedData?.first_name}
              accentColor={brandColor}
              isAdmin={isAdmin}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <GalleryTab
              deceasedId={currentStream.booking.deceased?.[0]?.id}
              isAdmin={isAdmin}
              isMobile={isMobile}
              colors={colors}
              socketRef={socketRef}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={6}>
            <DownloadsTab
              streamId={currentStream?.id?.toString() || ""}
              vimeoLink={currentStream?.vimeo_stream_link || ""}
              vimeoPassword={currentStream?.vimeo_download_password || ""}
              currentStream={currentStream}
            />
          </TabPanel>
        </Box>
      </Container>
    </Box>
  );
}
